import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { ISurveyTranslation } from "../../../@types/admin/AdminSurveyTranslation";
import { TableSorted } from "../../../components/TableSorted";
import { useUpdateSurveyTranslation } from "../../../hooks/mutations/admin/surveyTranslations";
import {
  useSurveyTranslations,
  useTranslatableSurveys,
} from "../../../hooks/queries/admin/surveyTranslations";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import {
  AdminSelectField,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { AddSurveyLanguageModal } from "./AddSurveyLanguageModal";
import {
  SelectionPrompt,
  TranslationKey,
  UnsavedActions,
  UnsavedBar,
  UnsavedCount,
} from "./AdminTranslations.styles";
import { DiscardTranslationsModal } from "./DiscardTranslationsModal";
import { translationRowId } from "./translationRows";
import { TranslationValueCell } from "./TranslationValueCell";

const SEARCH_FIELDS: (keyof ISurveyTranslation)[] = [
  "key",
  "languageCode",
  "usedLocation",
  "value",
];

/**
 * Survey Translations admin page (EN-5163), migrated from the Retool
 * SurveyTranslations app: pick a survey, edit its strings per language
 * inline, and seed a new language.
 *
 * Despite the name this is unrelated to the cross-environment Translations
 * work (EN-5178) — it is plain pMp API, as the ticket says.
 *
 * Retool edited one row at a time in a modal. The ticket asks for an inline
 * grid, which means edits accumulate, which means they can be lost — hence
 * the dirty-row tracking and the guard on switching survey.
 *
 * ⚠️ The endpoint saves ONE row per call, so Save issues one request per
 * edited row and reports how many landed. A row is identified by
 * `(key, languageCode, usedLocation)`; there is no id.
 */
export function AdminSurveyTranslations() {
  document.title = "Survey translations - Admin";

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { surveys, isLoading: surveysLoading } = useTranslatableSurveys();

  const [surveyId, setSurveyId] = useState<string>("");
  const { translations, isLoading, isError } = useSurveyTranslations(
    surveyId || undefined
  );
  const { updateTranslation, isUpdatingTranslation, invalidateTranslations } =
    useUpdateSurveyTranslation(surveyId || undefined);

  /** Edited values, keyed by row identity. Cleared on a successful save. */
  const [edits, setEdits] = useState<Record<string, string>>({});

  /**
   * Bumped to remount the value cells. Each cell holds the text you are
   * typing locally (see `TranslationValueCell`), so clearing `edits` alone
   * would leave the old text on screen — discarding, saving and switching
   * survey all have to reseed them from the server rows.
   */
  const [cellGeneration, setCellGeneration] = useState(0);
  const resetCells = useCallback(() => setCellGeneration((n) => n + 1), []);

  const dirtyCount = Object.keys(edits).length;
  const surveyName =
    surveys.find((survey) => survey.id === surveyId)?.name ?? "";

  /**
   * The table is fed the **server** rows, never the edited ones. Folding
   * `edits` in here rebuilt this array — and everything paging and sorting
   * derived from it — on every keystroke. The trade is that the search box
   * matches the stored value of a row you have edited but not yet saved,
   * which the highlighted cell already tells you about.
   */
  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: translations, searchFields: SEARCH_FIELDS });

  const existingCodes = useMemo(
    () => Array.from(new Set(translations.map((t) => t.languageCode))),
    [translations]
  );

  const setValue = useCallback(
    (translation: ISurveyTranslation, value: string) => {
      const id = translationRowId(translation);
      setEdits((previous) => {
        const next = { ...previous };
        // Typing a value back to what the server has isn't an edit any more.
        // `translation` is the server row, so its own value is the baseline.
        if ((translation.value ?? "") === value) {
          delete next[id];
        } else {
          next[id] = value;
        }
        return next;
      });
    },
    []
  );

  const discardEdits = () => {
    setEdits({});
    resetCells();
  };

  const switchSurvey = (nextSurveyId: string) => {
    setEdits({});
    resetCells();
    setSurveyId(nextSurveyId);
  };

  /**
   * Guards the unsaved edits. Retool refetched on survey change and the edits
   * simply vanished — the ticket calls this out explicitly.
   */
  const handleSurveyChange = (nextSurveyId: string) => {
    if (dirtyCount === 0) {
      switchSurvey(nextSurveyId);
      return;
    }

    openModal(
      <DiscardTranslationsModal
        dirtyCount={dirtyCount}
        onClose={closeModal}
        onDiscard={() => {
          switchSurvey(nextSurveyId);
          closeModal();
        }}
      />,
      { width: "440px", showCloseButton: true }
    );
  };

  const handleSave = async () => {
    const pending = translations
      .map((translation) => ({
        translation,
        id: translationRowId(translation),
      }))
      .filter(({ id }) => id in edits);

    if (pending.length === 0) return;

    // One request per row — the endpoint has no bulk form. Sequential so a
    // failure part-way leaves the rest of the edits intact on screen.
    const saved: string[] = [];
    let failed = 0;

    // eslint-disable-next-line no-restricted-syntax
    for (const { translation, id } of pending) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await updateTranslation({ ...translation, value: edits[id] });
        saved.push(id);
      } catch {
        failed += 1;
      }
    }

    if (saved.length > 0) {
      setEdits((previous) => {
        const next = { ...previous };
        saved.forEach((id) => delete next[id]);
        return next;
      });
      // The refetch reseeds the saved cells on its own — their key carries
      // the server value — but a row that failed still holds its edit.
      invalidateTranslations();
    }

    if (failed === 0) {
      showToast("The translation was updated successfully.", "success");
    } else {
      showToast(
        `${saved.length} of ${pending.length} translations saved — ${failed} failed and are still shown as unsaved.`,
        "warning"
      );
    }
  };

  const handleAddLanguage = () => {
    if (!surveyId) return;
    openModal(
      <AddSurveyLanguageModal
        surveyId={surveyId}
        surveyName={surveyName}
        existingCodes={existingCodes}
        onClose={closeModal}
      />,
      { width: "480px", showCloseButton: true }
    );
  };

  const columns: ColumnDef<ISurveyTranslation, string>[] = useMemo(
    () => [
      {
        accessorKey: "key",
        header: "Key",
        // A dynamic key is a name plus a GUID and runs past 50 characters;
        // left to size itself it took the width the Value editor needs.
        cell: ({ row }) => (
          <TranslationKey title={row.original.key}>
            {row.original.key}
          </TranslationKey>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "languageCode",
        header: "Language",
        enableSorting: true,
      },
      {
        accessorKey: "usedLocation",
        header: "Used location",
        enableSorting: true,
      },
      {
        id: "value",
        header: "Value",
        // Feeds the search; the cell renders the editor.
        accessorFn: (translation) => translation.value,
        // Without this an empty value renders TableSorted's "-" placeholder
        // instead of the editor, and a cleared translation (or one the API
        // serves blank) becomes uneditable — in a translation tool, exactly
        // the rows that need writing.
        meta: { renderEmpty: true },
        cell: ({ row }) => (
          <TranslationValueCell
            // The cell owns the text you type, so React has to be told when
            // to throw that away: `cellGeneration` covers Discard (the server
            // value is unchanged, only the local text has to go) and the
            // server value covers a refetch landing with someone else's save.
            key={`${translationRowId(row.original)}#${cellGeneration}#${
              row.original.value ?? ""
            }`}
            translation={row.original}
            disabled={isUpdatingTranslation}
            onCommit={setValue}
          />
        ),
        enableSorting: false,
      },
    ],
    // Deliberately not `edits`: a new `columns` array makes `useReactTable`
    // rebuild every column, which is exactly what made typing stall.
    [cellGeneration, isUpdatingTranslation, setValue]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Survey translations</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="translations-survey">Survey</FilterLabel>
          <AdminSelectField
            id="translations-survey"
            value={surveyId}
            disabled={surveysLoading}
            onChange={(e) => handleSurveyChange(e.target.value)}
          >
            <option value="">
              {surveysLoading ? "Loading..." : "Select a survey"}
            </option>
            {surveys.map((survey) => (
              <option key={survey.id} value={survey.id}>
                {survey.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        {surveyId && (
          <SearchInput
            type="text"
            placeholder="Search by key, language, location, or value..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}

        <FiltersActions>
          {/*
            Primary, like every other page's create action. It was the one
            "add" rendered as a secondary button, which is half of why the
            control read differently here.
          */}
          <FilterButton
            variant="primary"
            disabled={!surveyId}
            onClick={handleAddLanguage}
          >
            Add language
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load this survey&apos;s translations. Try refreshing the
          page.
        </ErrorMessage>
      )}

      {dirtyCount > 0 && (
        <UnsavedBar>
          <UnsavedCount>
            {dirtyCount} unsaved translation{dirtyCount === 1 ? "" : "s"}.
          </UnsavedCount>
          <UnsavedActions>
            <FilterButton
              variant="secondary"
              disabled={isUpdatingTranslation}
              onClick={discardEdits}
            >
              Discard
            </FilterButton>
            <FilterButton
              variant="primary"
              disabled={isUpdatingTranslation}
              onClick={handleSave}
            >
              {isUpdatingTranslation ? "Saving..." : "Save changes"}
            </FilterButton>
          </UnsavedActions>
        </UnsavedBar>
      )}

      {!surveyId ? (
        <SelectionPrompt>
          Choose a survey to see and edit its translations.
        </SelectionPrompt>
      ) : (
        <TableContainer>
          <TableWrapper refreshKeys={[["admin-survey-translations"]]}>
            <TableSorted
              columns={columns}
              data={items}
              emptyStateText={
                isLoading ? <TableLoadingState /> : "No rows found"
              }
            />
          </TableWrapper>
          <TablePagination
            exportCsv={{
              filename: "survey-translations",
              columns,
              rows: allItems,
            }}
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </TableContainer>
      )}
    </PageContainer>
  );
}
